import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BellNotificationBubbleProps {
  message: string;
  onClose: () => void;
  duration?: number;
}

/**
 * BellNotificationBubble Component
 * Arcade-style notification bubble that appears from the notification bell
 * Auto-closes after duration (default 5s) or when X is clicked
 */
export function BellNotificationBubble({ message, onClose, duration = 5000 }: BellNotificationBubbleProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Auto-close after duration
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for exit animation
  };

  return (
    <>
      {/* Arcade animation keyframes */}
      <style>{`
        @keyframes bellBubbleSlideIn {
          0% {
            transform: translateY(-20px) scale(0.8);
            opacity: 0;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes bellBubbleSlideOut {
          0% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateY(-20px) scale(0.8);
            opacity: 0;
          }
        }
      `}</style>

      <div
        className="fixed z-[9999]"
        style={{
          top: '70px',
          right: '20px',
          animation: isVisible
            ? 'bellBubbleSlideIn 0.3s ease-out forwards'
            : 'bellBubbleSlideOut 0.3s ease-in forwards',
        }}
      >
        {/* Arcade bubble */}
        <div
          className="relative p-4 min-w-[320px] max-w-[400px]"
          style={{
            backgroundColor: 'var(--theme-card-yellow)',
            border: '4px solid var(--theme-border)',
            boxShadow: '8px 8px 0px var(--theme-border)',
            color: 'var(--theme-text)',
          }}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 p-1 transition-transform hover:scale-110"
            style={{
              backgroundColor: 'var(--theme-card-pink)',
              border: '2px solid var(--theme-border)',
              boxShadow: '2px 2px 0px var(--theme-border)',
              color: 'var(--theme-text)',
            }}
            aria-label={t('common:a11y.closeNotification')}
          >
            <X className="h-3 w-3" />
          </button>

          {/* Message */}
          <p
            className="text-sm font-bold uppercase pr-8"
            style={{
              fontFamily: 'var(--font-pixel), monospace',
              lineHeight: '1.5',
            }}
          >
            {message}
          </p>
        </div>

        {/* Arcade pointer arrow pointing to bell */}
        <div
          className="absolute"
          style={{
            top: '-12px',
            right: '60px',
            width: 0,
            height: 0,
            borderLeft: '12px solid transparent',
            borderRight: '12px solid transparent',
            borderBottom: '12px solid var(--theme-border)',
          }}
        >
          {/* Inner arrow */}
          <div
            style={{
              position: 'absolute',
              top: '4px',
              left: '-8px',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderBottom: '8px solid var(--theme-card-yellow)',
            }}
          />
        </div>
      </div>
    </>
  );
}
